import { useEffect, useRef, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Check, Plus, X } from '../../../../../components/icon/solar-icons.js'

export interface PickerEmployee {
    id: number
    first_name: string
    last_name: string
    avatar_url: string | null
    job_title: string | null
}

export interface PolicyEmployeePickerProps {
    lang: Language
    selectedIds: number[]
    selectedEmployees: PickerEmployee[]
    onAdd: (employee: PickerEmployee) => void
    onRemove: (id: number) => void
    onSelectAll: (employees: PickerEmployee[]) => void
    onClearAll: () => void
}

export const PolicyEmployeePicker = (props: PolicyEmployeePickerProps) => {
    const { lang, selectedIds, selectedEmployees } = props
    const s = t.portal.settings.policies

    const [query, setQuery] = useState('')
    const [results, setResults] = useState<PickerEmployee[]>([])
    const [loading, setLoading] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const initials = (first: string, last: string) =>
        `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()

    // Fetch employees on search query change (debounced)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            void (async () => {
                setLoading(true)
                try {
                    const searchParam = query ? `&search=${encodeURIComponent(query)}` : ''
                    const res = await fetch(`/api/portal/employees?limit=30${searchParam}`)
                    if (res.ok) {
                        const body = (await res.json()) as {
                            data: PickerEmployee[]
                        }
                        setResults(body.data)
                    }
                } catch {
                    /* silently fail */
                } finally {
                    setLoading(false)
                }
            })()
        }, 300)

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [query])

    const isSelected = (id: number) => selectedIds.includes(id)

    const handleToggle = (emp: PickerEmployee) => {
        if (isSelected(emp.id)) {
            props.onRemove(emp.id)
        } else {
            props.onAdd(emp)
        }
    }

    return (
        <div className="portal-employee-picker">
            {/* Header with count + bulk actions */}
            <div className="portal-employee-picker__header">
                <span className="portal-employee-picker__count">
                    {s.selectedCount(selectedIds.length)(lang)}
                </span>
                <div className="portal-employee-picker__bulk-actions">
                    {results.length > 0 ? (
                        <button
                            type="button"
                            className="portal-employee-picker__bulk-btn"
                            onClick={() => props.onSelectAll(results)}
                        >
                            {s.selectAll(lang)}
                        </button>
                    ) : null}
                    {selectedIds.length > 0 ? (
                        <button
                            type="button"
                            className="portal-employee-picker__bulk-btn"
                            onClick={props.onClearAll}
                        >
                            {s.clearAll(lang)}
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Selected employees as chips */}
            <div className="portal-employee-picker__selected">
                {selectedEmployees.length === 0 ? (
                    <span className="portal-employee-picker__selected-empty">
                        {s.noEmployeesAssigned(lang)}
                    </span>
                ) : (
                    selectedEmployees.map((emp) => (
                        <span key={emp.id} className="portal-employee-picker__chip">
                            {emp.avatar_url ? (
                                <img
                                    className="portal-employee-picker__chip-avatar"
                                    src={emp.avatar_url}
                                    alt=""
                                />
                            ) : (
                                <span className="portal-employee-picker__chip-avatar portal-employee-picker__chip-avatar--placeholder">
                                    {initials(emp.first_name, emp.last_name)}
                                </span>
                            )}
                            {emp.first_name} {emp.last_name}
                            <button
                                type="button"
                                className="portal-employee-picker__chip-remove"
                                onClick={() => props.onRemove(emp.id)}
                            >
                                <X size={14} />
                            </button>
                        </span>
                    ))
                )}
            </div>

            {/* Search */}
            <div className="portal-employee-picker__search">
                <TextField
                    className="portal-employee-picker__search-input"
                    value={query}
                    onChange={(value) => setQuery(value)}
                    placeholder={s.searchEmployees(lang)}
                />
            </div>

            {/* Available employees list */}
            <div className="portal-employee-picker__list">
                {loading ? (
                    <div className="portal-employee-picker__loading">…</div>
                ) : results.length === 0 ? (
                    <div className="portal-employee-picker__empty">{s.noEmployeesFound(lang)}</div>
                ) : (
                    results.map((emp) => {
                        const selected = isSelected(emp.id)
                        return (
                            <div
                                key={emp.id}
                                className={`portal-employee-picker__item ${selected ? 'is-selected' : ''}`}
                                onClick={() => handleToggle(emp)}
                            >
                                {emp.avatar_url ? (
                                    <img
                                        className="portal-employee-picker__item-avatar"
                                        src={emp.avatar_url}
                                        alt=""
                                    />
                                ) : (
                                    <span className="portal-employee-picker__item-avatar portal-employee-picker__item-avatar--placeholder">
                                        {initials(emp.first_name, emp.last_name)}
                                    </span>
                                )}
                                <div className="portal-employee-picker__item-info">
                                    <div className="portal-employee-picker__item-name">
                                        {emp.first_name} {emp.last_name}
                                    </div>
                                    {emp.job_title ? (
                                        <div className="portal-employee-picker__item-title">
                                            {emp.job_title}
                                        </div>
                                    ) : null}
                                </div>
                                <span className="portal-employee-picker__item-action">
                                    {selected ? <Check size={14} /> : <Plus size={14} />}
                                </span>
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )
}
